import { useState } from 'react'

import { useHistory } from 'react-router'

import { MemberEmailStatus } from '@tribeplatform/gql-client/types'
import { T, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { ProfileSettingsModal } from '@tribeplatform/react-components/ProfileSettingsModal'
import { CreateSpaceFromTemplateModal as CreateSpaceModal } from '@tribeplatform/react-components/Space'
import {
  useAuthMember,
  useAuthToken,
  useMemberPosts,
  useSpaces,
} from '@tribeplatform/react-sdk/hooks'
import { useSlate } from '@tribeplatform/react-slate-kit/hooks'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Carousel } from '@tribeplatform/react-ui-kit/Carousel'

import { VerifyModal } from '../Auth/VerifyModal'
import { UserImportModal } from '../UserImport'
import { MemberOnboardingPlaceholder } from './MemberOnboardingPlaceholder'
import { MemberOnboardingStep } from './Step'
import {
  ConfirmEmailStep,
  CreatePostStep,
  ProfileStep,
  StructureStep,
  UploadLogoStep,
} from './Steps'

export const MemberOnboarding = () => {
  const {
    data: { network },
  } = useAuthToken()
  const { mode } = useSlate()
  const { data: member, isAdmin, isGuest } = useAuthMember()

  const { data: spaces, isLoading: isSpacesLoading } = useSpaces({
    fields: {},
    useInfiniteQueryOptions: {
      enabled: isAdmin,
    },
    variables: {
      limit: 0,
    },
  })

  const { data: memberPosts, isLoading: isMemberPostLoading } = useMemberPosts({
    variables: { limit: 0, memberId: member?.id },
    useInfiniteQueryOptions: {
      refetchOnMount: 'always',
      enabled: isAdmin && !!member?.id,
    },
    fields: 'basic',
  })

  const [profileModal, setProfileModal] = useState(false)
  const [verifyModal, setVerifyModal] = useState(false)
  const [spaceModal, setSpaceModal] = useState(false)
  const [inviteModal, setInviteModal] = useState(false)

  const { push } = useHistory()

  const confirmEmailStep = useStaticIntl(
    ConfirmEmailStep,
    member.emailStatus === MemberEmailStatus.verified,
    () => {
      setVerifyModal(true)
    },
  )

  const uploadLogoStep = useStaticIntl(
    UploadLogoStep,
    !!(network?.logoId || network?.faviconId),
    () => {
      push('/manage/settings/network')
    },
  )

  const structureStep = useStaticIntl(
    StructureStep,
    spaces?.pages?.[0]?.totalCount > 1,
    () => {
      setSpaceModal(true)
    },
  )

  const profileStep = useStaticIntl(
    ProfileStep,
    !!(member?.profilePictureId || member?.tagline),
    () => {
      setProfileModal(true)
    },
  )

  const createPostStep = useStaticIntl(
    CreatePostStep,
    memberPosts?.pages?.[0].totalCount > 0,
    () => {
      push('/new')
    },
  )

  if (mode === 'edit') {
    return <MemberOnboardingPlaceholder />
  }
  if (isGuest || !isAdmin) {
    return null
  }
  if (isSpacesLoading || isMemberPostLoading) {
    return null
  }

  const steps = []

  if (isAdmin) {
    steps.push(confirmEmailStep)
    steps.push(uploadLogoStep)
    steps.push(structureStep)
  }

  steps.push(profileStep)
  steps.push(createPostStep)

  if (steps?.filter(step => !step.completed)?.length === 0) {
    return null
  }

  steps.sort((a, b) => {
    if (a.completed && !b.completed) {
      return 1
    }
    if (b.completed && !a.completed) {
      return -1
    }
    return a.order > b.order ? 1 : -1
  })

  return (
    <>
      <Card className="overflow-hidden">
        <Card.Header
          title={
            isAdmin ? (
              <T
                defaultMessage="Setup your {COMMUNITY}"
                id="Onboarding.Title.Admin"
              />
            ) : (
              <T
                defaultMessage="Complete your profile"
                id="Onboarding.Title.NonAdmin"
              />
            )
          }
        />
        <Card.Content>
          <Carousel
            className="pt-1 pb-4"
            items={steps.map(step => (
              <MemberOnboardingStep step={step} key={step.title} />
            ))}
          />
        </Card.Content>
      </Card>
      {profileModal && (
        <ProfileSettingsModal
          member={member}
          open={profileModal}
          onClose={() => setProfileModal(false)}
        />
      )}
      {verifyModal && (
        <VerifyModal open={verifyModal} onClose={() => setVerifyModal(false)} />
      )}
      {spaceModal && (
        <CreateSpaceModal
          open={spaceModal}
          onClose={() => setSpaceModal(false)}
        />
      )}
      {inviteModal && (
        <UserImportModal
          open={inviteModal}
          onClose={() => {
            setInviteModal(false)
          }}
        />
      )}
    </>
  )
}

MemberOnboarding.displayName = 'Member Onboarding'
